// Google Ads attribution as the tracker sees it: the app stamps the Play
// install referrer's gclid into sub_id_4 and gbraid into sub_id_3 before
// opening the force offer. sub_id_4 = '123456789' is the app's placeholder
// when there was no gclid, so it is not shown as one.

export const GCLID_PLACEHOLDER = '123456789';

export interface GoogleAdsIds {
  gclid: string | null;
  gbraid: string | null;
}

export function googleAdsIds(sub3: string | null | undefined, sub4: string | null | undefined): GoogleAdsIds {
  const gclid = sub4 && sub4 !== GCLID_PLACEHOLDER ? sub4 : null;
  const gbraid = sub3 || null;
  return { gclid, gbraid };
}

function shortId(id: string) {
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

/** Table cell: badge + shortened ids, or a dash. */
export function GoogleAdsCell({ sub3, sub4 }: { sub3: string | null | undefined; sub4: string | null | undefined }) {
  const { gclid, gbraid } = googleAdsIds(sub3, sub4);
  if (!gclid && !gbraid) return <span className="text-xs text-content-subtle">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="px-1.5 py-0.5 rounded text-[11px] font-medium w-fit bg-sky-500/15 text-sky-500">Google Ads</span>
      {gclid && (
        <span className="text-[11px] text-content-subtle font-mono" title={`gclid: ${gclid}`}>gclid {shortId(gclid)}</span>
      )}
      {gbraid && (
        <span className="text-[11px] text-content-subtle font-mono" title={`gbraid: ${gbraid}`}>gbraid {shortId(gbraid)}</span>
      )}
    </div>
  );
}

export interface Ga4Send {
  status?: string | null;
  eventName?: string | null;
  attempts?: number | null;
  lastError?: string | null;
}

const GA4_STATUS: Record<string, { text: string; cls: string }> = {
  success: { text: 'Sent', cls: 'text-success' },
  pending: { text: 'Queued', cls: 'text-content-muted' },
  failed: { text: 'Retrying', cls: 'text-warning' },
  dead: { text: 'Dropped', cls: 'text-danger' },
};

/**
 * Details-modal block with the full ids, plus the Firebase side: the app's
 * app_instance_id and, on a conversion, how its send to GA4 went.
 */
export function GoogleAdsDetails({ sub3, sub4, appInstanceId, ga4 }: {
  sub3: string | null | undefined;
  sub4: string | null | undefined;
  appInstanceId?: string | null;
  ga4?: Ga4Send;
}) {
  const { gclid, gbraid } = googleAdsIds(sub3, sub4);
  const st = ga4?.status ? GA4_STATUS[ga4.status] : undefined;
  return (
    <div className="border-t pt-3 mt-3">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle mb-2">Google Ads</div>
      {(appInstanceId || ga4) && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-content-subtle">app_instance_id</div>
            <div className="text-sm text-content break-all font-mono">{appInstanceId || '—'}</div>
          </div>
          {ga4 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-content-subtle">Firebase conversion</div>
              <div className={`text-sm ${st?.cls ?? 'text-content-subtle'}`}>
                {st ? `${st.text}${ga4.eventName ? ` · ${ga4.eventName}` : ''}${ga4.attempts ? ` · ${ga4.attempts} attempt(s)` : ''}` : 'Not sent (no app_instance_id)'}
              </div>
              {ga4.lastError && ga4.status !== 'success' && (
                <div className="text-xs text-content-subtle break-words">{ga4.lastError}</div>
              )}
            </div>
          )}
        </div>
      )}
      {!gclid && !gbraid ? (
        <div className="text-xs text-content-subtle">Not from Google Ads</div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-content-subtle">gclid (sub_id_4)</div>
            <div className="text-sm text-content break-all font-mono">{gclid || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-content-subtle">gbraid (sub_id_3)</div>
            <div className="text-sm text-content break-all font-mono">{gbraid || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export const GOOGLE_ADS_FILTER_OPTIONS = [
  { value: '', label: 'All traffic' },
  { value: '1', label: 'Google Ads only' },
  { value: '0', label: 'Not Google Ads' },
];
